import React, { useState } from 'react';

export type ToolType = 
  | 'select' 
  | 'sticky' 
  | 'text' 
  | 'connector' 
  | 'emoji' 
  | 'pen' 
  | 'shape' 
  | 'frame';

export type ShapeType = 'rectangle' | 'circle' | 'triangle' | 'diamond' | 'arrow';

export interface StickyColor {
  name: string;
  bg: string;
  border: string;
  text: string;
}

export const STICKY_COLORS: StickyColor[] = [
  // Purples
  { name: 'Lavender', bg: '#E9D5FF', border: '#C084FC', text: '#581C87' },
  { name: 'Purple Light', bg: '#DDD6FE', border: '#A78BFA', text: '#5B21B6' },
  { name: 'Violet', bg: '#C4B5FD', border: '#8B5CF6', text: '#4C1D95' },
  // Blues
  { name: 'Sky', bg: '#BAE6FD', border: '#38BDF8', text: '#0C4A6E' },
  { name: 'Cyan', bg: '#A5F3FC', border: '#22D3EE', text: '#155E75' },
  { name: 'Blue Light', bg: '#BFDBFE', border: '#60A5FA', text: '#1E40AF' },
  // Greens
  { name: 'Mint', bg: '#A7F3D0', border: '#34D399', text: '#064E3B' },
  { name: 'Green', bg: '#BBF7D0', border: '#4ADE80', text: '#14532D' },
  { name: 'Lime', bg: '#D9F99D', border: '#A3E635', text: '#365314' },
  // Yellows
  { name: 'Lemon', bg: '#FEF3C7', border: '#FBBF24', text: '#78350F' },
  { name: 'Yellow', bg: '#FEF08A', border: '#FACC15', text: '#713F12' },
  { name: 'Amber', bg: '#FDE68A', border: '#F59E0B', text: '#78350F' },
  // Oranges & Reds
  { name: 'Orange', bg: '#FED7AA', border: '#FB923C', text: '#7C2D12' },
  { name: 'Peach', bg: '#FECACA', border: '#F87171', text: '#7F1D1D' },
  { name: 'Pink', bg: '#FBCFE8', border: '#F472B6', text: '#831843' },
  { name: 'Rose', bg: '#FECDD3', border: '#FB7185', text: '#881337' },
  // Neutrals
  { name: 'Gray', bg: '#E5E7EB', border: '#9CA3AF', text: '#1F2937' },
  { name: 'White', bg: '#FFFFFF', border: '#D1D5DB', text: '#374151' },
];

// Common emojis for retrospectives
export const EMOJI_LIST = [
  '👍', '👎', '❤️', '🔥', '⭐', '💡', '🎯', '🚀',
  '✅', '❌', '⚠️', '💪', '🤔', '😀', '😢', '😡',
  '🎉', '👏', '💯', '🏆', '📌', '🔧', '📈', '📉',
];

// Pen colors
export const PEN_COLORS = [
  '#000000', '#EF4444', '#F97316', '#EAB308', 
  '#22C55E', '#14B8A6', '#3B82F6', '#8B5CF6',
];

interface CanvasToolbarProps {
  selectedTool: ToolType;
  onToolSelect: (tool: ToolType) => void;
  selectedColor: StickyColor;
  onColorSelect: (color: StickyColor) => void;
  onAddSticky: (color: StickyColor) => void;
  onAddEmoji?: (emoji: string) => void;
  onAddText?: () => void;
  onAddShape?: (shape: ShapeType) => void;
  onAddFrame?: () => void;
  penColor?: string;
  onPenColorChange?: (color: string) => void;
  penSize?: number;
  onPenSizeChange?: (size: number) => void;
}

const CanvasToolbar: React.FC<CanvasToolbarProps> = ({
  selectedTool,
  onToolSelect,
  selectedColor,
  onColorSelect,
  onAddSticky,
  onAddEmoji,
  onAddText,
  onAddShape,
  onAddFrame,
  penColor = '#000000',
  onPenColorChange,
  penSize = 3,
  onPenSizeChange,
}) => {
  const [showStickyPalette, setShowStickyPalette] = useState(false);
  const [showEmojiPalette, setShowEmojiPalette] = useState(false);
  const [showShapePalette, setShowShapePalette] = useState(false);
  const [showPenSettings, setShowPenSettings] = useState(false);

  const tools: { id: ToolType; icon: React.ReactNode; label: string }[] = [
    {
      id: 'select',
      label: 'Select',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
          <path d="M13 13l6 6" />
        </svg>
      ),
    },
    {
      id: 'sticky',
      label: 'Sticky Note',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="12" y2="17" />
        </svg>
      ),
    },
    {
      id: 'emoji',
      label: 'Emoji',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      ),
    },
    {
      id: 'text',
      label: 'Text',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="4 7 4 4 20 4 20 7" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <line x1="12" y1="4" x2="12" y2="20" />
        </svg>
      ),
    },
    {
      id: 'pen',
      label: 'Draw',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 19l7-7 3 3-7 7-3-3z" />
          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
          <path d="M2 2l7.586 7.586" />
          <circle cx="11" cy="11" r="2" />
        </svg>
      ),
    },
    {
      id: 'connector',
      label: 'Connector',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      ),
    },
    {
      id: 'shape',
      label: 'Shapes',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      ),
    },
    {
      id: 'frame',
      label: 'Frame',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
      ),
    },
  ];

  const shapes: { id: ShapeType; icon: React.ReactNode; label: string }[] = [
    {
      id: 'rectangle',
      label: 'Rectangle',
      icon: <rect x="3" y="5" width="18" height="14" rx="2" />,
    },
    {
      id: 'circle',
      label: 'Circle',
      icon: <circle cx="12" cy="12" r="9" />,
    },
    {
      id: 'triangle',
      label: 'Triangle',
      icon: <polygon points="12 3 3 21 21 21" />,
    },
    {
      id: 'diamond',
      label: 'Diamond',
      icon: <polygon points="12 2 22 12 12 22 2 12" />,
    },
    {
      id: 'arrow',
      label: 'Arrow',
      icon: (
        <>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </>
      ),
    },
  ];

  const handleToolClick = (toolId: ToolType) => {
    // Close all palettes first
    setShowStickyPalette(false);
    setShowEmojiPalette(false);
    setShowShapePalette(false);
    setShowPenSettings(false);

    // Handle tool-specific actions
    switch (toolId) {
      case 'sticky':
        setShowStickyPalette(true);
        break;
      case 'emoji':
        setShowEmojiPalette(true);
        break;
      case 'shape':
        setShowShapePalette(true);
        break;
      case 'pen':
        setShowPenSettings(true);
        break;
      case 'text':
        onAddText?.();
        break;
      case 'frame':
        onAddFrame?.();
        break;
    }

    onToolSelect(toolId);
  };

  const closePalettes = () => {
    setShowStickyPalette(false);
    setShowEmojiPalette(false);
    setShowShapePalette(false);
    setShowPenSettings(false);
  };

  return (
    <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50 flex gap-2">
      {/* Main Toolbar */}
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-1.5 flex flex-col gap-0.5">
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => handleToolClick(tool.id)}
            className={`
              p-2.5 rounded-xl transition-all duration-150 relative group
              ${selectedTool === tool.id
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
              }
            `}
            title={tool.label}
          >
            {tool.icon}

            {/* Tooltip */}
            <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 shadow-lg">
              {tool.label}
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-slate-800" />
            </div>
          </button>
        ))}

        {/* Divider */}
        <div className="h-px bg-slate-100 my-1 mx-1" />

        {/* Quick Add Sticky Button */}
        <button
          onClick={() => onAddSticky(selectedColor)}
          className="p-2.5 rounded-xl text-orange-500 hover:bg-orange-50 hover:text-orange-600 transition-all duration-150 group relative"
          title="Quick Add Sticky"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>

          {/* Tooltip */}
          <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 shadow-lg">
            Quick Add Sticky
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-slate-800" />
          </div>
        </button>
      </div>

      {/* Sticky Color Palette */}
      {showStickyPalette && (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 animate-fade-in max-w-[320px] relative">
          <button
            onClick={closePalettes}
            className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Sticky Colors
          </p>

          <div className="grid grid-cols-6 gap-2">
            {STICKY_COLORS.map((color, index) => (
              <button
                key={index}
                onClick={() => {
                  onColorSelect(color);
                  onAddSticky(color);
                  closePalettes();
                }}
                className={`
                  w-10 h-10 rounded-lg transition-all duration-200 hover:scale-110
                  hover:shadow-lg active:scale-95 border-2
                  ${selectedColor.bg === color.bg
                    ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-white'
                    : ''
                  }
                `}
                style={{
                  backgroundColor: color.bg,
                  borderColor: color.border
                }}
                title={color.name}
              />
            ))}
          </div>
        </div>
      )}

      {/* Emoji Palette */}
      {showEmojiPalette && (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 animate-fade-in relative">
          <button
            onClick={closePalettes}
            className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Add Emoji
          </p>

          <div className="grid grid-cols-8 gap-2">
            {EMOJI_LIST.map((emoji, index) => (
              <button
                key={index}
                onClick={() => {
                  onAddEmoji?.(emoji);
                  closePalettes();
                }}
                className="w-10 h-10 rounded-lg transition-all duration-200 hover:scale-125 hover:bg-orange-50 active:scale-95 flex items-center justify-center text-2xl"
                title={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Shape Palette */}
      {showShapePalette && (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 animate-fade-in relative">
          <button
            onClick={closePalettes}
            className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Add Shape
          </p>

          <div className="flex gap-2">
            {shapes.map((shape) => (
              <button
                key={shape.id}
                onClick={() => {
                  onAddShape?.(shape.id);
                  closePalettes();
                }}
                className="w-12 h-12 rounded-lg transition-all duration-200 hover:scale-110 hover:bg-orange-50 active:scale-95 flex items-center justify-center text-slate-500 hover:text-orange-600"
                title={shape.label}
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  {shape.icon}
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pen Settings */}
      {showPenSettings && (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 animate-fade-in relative">
          <button
            onClick={closePalettes}
            className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Pen Color
          </p>

          <div className="grid grid-cols-4 gap-2 mb-4">
            {PEN_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => onPenColorChange?.(color)}
                className={`
                  w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95
                  ${penColor === color ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-white' : ''}
                `}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Brush Size
          </p>

          <div className="flex items-center gap-3">
            {[2, 4, 6, 8].map((size) => (
              <button
                key={size}
                onClick={() => onPenSizeChange?.(size)}
                className={`
                  flex items-center justify-center w-10 h-10 rounded-lg transition-all
                  ${penSize === size
                    ? 'bg-orange-100 border border-orange-200'
                    : 'hover:bg-orange-50'
                  }
                `}
              >
                <div
                  className="rounded-full bg-slate-700"
                  style={{ width: size * 2, height: size * 2 }}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CanvasToolbar;
